import type { Entry } from "@/shared/types/entries";
import { parseEntryDate } from "@/v2/lib/dates";

interface TimeParts {
	hour: number;
	minute: number;
	second: number;
}

const TIME_ONLY_PATTERN = /^(\d{1,2})(?::(\d{2}))?(?::(\d{2})(?:\.\d+)?)?$/;

export function getEntryEndTimeParts(time?: string): TimeParts | null {
	if (!time) return null;

	if (time.includes("T")) {
		const date = new Date(time);
		if (Number.isNaN(date.getTime())) return null;
		return {
			hour: date.getUTCHours(),
			minute: date.getUTCMinutes(),
			second: date.getUTCSeconds(),
		};
	}

	const match = TIME_ONLY_PATTERN.exec(time.trim());
	if (!match) return null;

	const hour = Number.parseInt(match[1] ?? "", 10);
	const minute = Number.parseInt(match[2] ?? "0", 10);
	const second = Number.parseInt(match[3] ?? "0", 10);

	if (
		Number.isNaN(hour) ||
		Number.isNaN(minute) ||
		Number.isNaN(second) ||
		hour < 0 ||
		hour > 23 ||
		minute < 0 ||
		minute > 59 ||
		second < 0 ||
		second > 59
	) {
		return null;
	}

	return { hour, minute, second };
}

export function getEntryEndHour(time?: string): number | null {
	return getEntryEndTimeParts(time)?.hour ?? null;
}

/**
 * Numeric sort key for end_time values.
 *
 * ISO strings sort by their UTC timestamp. HH:mm/HH:mm:ss values are combined
 * with the entry's local date so date ranges sort chronologically instead of
 * lexicographically by the time text alone.
 */
export function getEntryEndTimeSortValue(entry: Entry): number | undefined {
	if (!entry.end_time) return undefined;

	if (entry.end_time.includes("T")) {
		const timestamp = Date.parse(entry.end_time);
		return Number.isNaN(timestamp) ? undefined : timestamp;
	}

	const parts = getEntryEndTimeParts(entry.end_time);
	if (!parts) return undefined;

	if (entry.date) {
		const entryDate = parseEntryDate(entry.date);
		if (!Number.isNaN(entryDate.getTime())) {
			entryDate.setHours(parts.hour, parts.minute, parts.second, 0);
			return entryDate.getTime();
		}
	}

	return (parts.hour * 60 + parts.minute) * 60 + parts.second;
}
